import { useState, useEffect } from 'react'
import { useRoles } from '../../hooks/useRoles'
import { useAdvisors } from '../../hooks/useAdvisors'
import { supabase } from '../../services/supabase'
import { Modal } from '../../components/Common/Modal'
import { Button } from '../../components/Common/Button'
import { PERMISSIONS_CONFIG, getSectionPermissionKeys, getModulePermissionKeys } from '../../config/permissions'
import { ChevronDown, ChevronRight } from 'lucide-react'

const TABS = [
  { id: 'details', label: 'Details' },
  { id: 'users', label: 'Assigned Users' },
  { id: 'permissions', label: 'Permissions' },
]

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none cursor-pointer ${
        checked ? 'bg-sfp-orange' : 'bg-gray-300'
      }`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`} />
    </button>
  )
}

/**
 * Get status indicator for module/section
 * Returns: 'all' | 'some' | 'none'
 */
function getStatus(permissionKeys, perms) {
  const enabled = permissionKeys.filter(key => perms[key]).length
  if (enabled === 0) return 'none'
  if (enabled === permissionKeys.length) return 'all'
  return 'some'
}

export function RoleForm({ initialData, onClose, sidebarCollapsed, isDarkMode = false }) {
  const [activeTab, setActiveTab] = useState('details')
  const [saving, setSaving] = useState(false)
  const [savingPerms, setSavingPerms] = useState(false)
  const [permsSaved, setPermsSaved] = useState(false)
  const [permsLoading, setPermsLoading] = useState(false)
  const [error, setError] = useState('')
  const [savedRoleId, setSavedRoleId] = useState(initialData?.id || null)

  const [details, setDetails] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
  })

  const [perms, setPerms] = useState({})
  const [assignedAdvisors, setAssignedAdvisors] = useState(new Set())
  const [search, setSearch] = useState('')
  const [expandedModules, setExpandedModules] = useState(new Set())
  const [expandedSections, setExpandedSections] = useState(new Set())

  const { createRole, updateRole, upsertPermission, assignAdvisor, unassignAdvisor } = useRoles()
  const { advisors, loading: advisorsLoading } = useAdvisors()

  // Load permissions and advisor assignments
  useEffect(() => {
    if (!savedRoleId) return

    setPermsLoading(true)

    // Load permissions
    supabase
      .from('role_permissions')
      .select('page_key, can_view')
      .eq('role_id', savedRoleId)
      .then(({ data, error }) => {
        if (error) { console.error('load permissions error:', error); return }
        const map = {}
        data?.forEach(p => { map[p.page_key] = p.can_view })
        setPerms(map)
        setPermsSaved(true)
      })
      .finally(() => setPermsLoading(false))

    // Load advisor assignments
    supabase
      .from('advisor_roles')
      .select('advisor_id')
      .eq('role_id', savedRoleId)
      .then(({ data, error }) => {
        if (error) { console.error('load advisor_roles error:', error); return }
        setAssignedAdvisors(new Set(data?.map(r => r.advisor_id) || []))
      })
  }, [savedRoleId])

  const handleSaveDetails = async () => {
    if (!details.name.trim()) { setError('Role name is required'); return }
    setError('')
    setSaving(true)
    try {
      if (savedRoleId) {
        await updateRole(savedRoleId, { name: details.name.trim(), description: details.description })
      } else {
        const created = await createRole({ name: details.name.trim(), description: details.description })
        setSavedRoleId(created.id)
      }
      setActiveTab('users')
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handlePermissionToggle = (permissionKey, newValue) => {
    setPerms(prev => ({ ...prev, [permissionKey]: newValue }))
    setPermsSaved(false)
  }

  const handleSectionToggle = (sectionId, enable) => {
    const keys = getSectionPermissionKeys(sectionId)
    const newPerms = { ...perms }
    keys.forEach(key => { newPerms[key] = enable })
    setPerms(newPerms)
    setPermsSaved(false)
  }

  const handleModuleToggle = (moduleId, enable) => {
    const keys = getModulePermissionKeys(moduleId)
    const newPerms = { ...perms }
    keys.forEach(key => { newPerms[key] = enable })
    setPerms(newPerms)
    setPermsSaved(false)
  }

  const handleSavePermissions = async () => {
    setSavingPerms(true)
    try {
      // Get all permission keys
      const allKeys = new Set()
      PERMISSIONS_CONFIG.modules.forEach(module => {
        module.sections.forEach(section => {
          section.permissions.forEach(perm => allKeys.add(perm.key))
        })
      })

      // Save all permissions (checked or unchecked)
      await Promise.all(
        [...allKeys].map(key => upsertPermission(savedRoleId, key, !!perms[key]))
      )
      setPermsSaved(true)
    } catch (err) {
      console.error('Failed to save permissions:', err)
    } finally {
      setSavingPerms(false)
    }
  }

  const handleAdvisorToggle = async (advisorId, isCurrentlyAssigned) => {
    const next = new Set(assignedAdvisors)
    if (isCurrentlyAssigned) {
      next.delete(advisorId)
      setAssignedAdvisors(next)
      try { await unassignAdvisor(savedRoleId, advisorId) }
      catch (err) { setAssignedAdvisors(assignedAdvisors); console.error(err) }
    } else {
      next.add(advisorId)
      setAssignedAdvisors(next)
      try { await assignAdvisor(savedRoleId, advisorId) }
      catch (err) { setAssignedAdvisors(assignedAdvisors); console.error(err) }
    }
  }

  const toggleModule = (moduleId) => {
    const next = new Set(expandedModules)
    next.has(moduleId) ? next.delete(moduleId) : next.add(moduleId)
    setExpandedModules(next)
  }

  const toggleSection = (sectionId) => {
    const next = new Set(expandedSections)
    next.has(sectionId) ? next.delete(sectionId) : next.add(sectionId)
    setExpandedSections(next)
  }

  const expandAll = () => {
    const allModules = new Set(PERMISSIONS_CONFIG.modules.map(m => m.id))
    const allSections = new Set()
    PERMISSIONS_CONFIG.modules.forEach(m => m.sections.forEach(s => allSections.add(s.id)))
    setExpandedModules(allModules)
    setExpandedSections(allSections)
  }

  const collapseAll = () => {
    setExpandedModules(new Set())
    setExpandedSections(new Set())
  }

  // Filter modules and sections by search
  const filteredModules = PERMISSIONS_CONFIG.modules.filter(module => {
    if (!search.trim()) return true
    const lowerSearch = search.toLowerCase()
    return (
      module.name.toLowerCase().includes(lowerSearch) ||
      module.sections.some(section =>
        section.name.toLowerCase().includes(lowerSearch) ||
        section.permissions.some(perm => perm.label.toLowerCase().includes(lowerSearch))
      )
    )
  })

  const notSavedYet = !savedRoleId

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={savedRoleId ? `Edit Role: ${details.name}` : 'Create Role'}
      sidebarCollapsed={sidebarCollapsed}
      isDarkMode={isDarkMode}
    >
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => !notSavedYet || tab.id === 'details' ? setActiveTab(tab.id) : null}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              activeTab === tab.id
                ? 'border-sfp-orange text-sfp-orange'
                : notSavedYet && tab.id !== 'details'
                ? 'border-transparent text-gray-300 cursor-not-allowed'
                : 'border-transparent text-gray-500 hover:text-sfp-darkgray'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Details */}
      {activeTab === 'details' && (
        <div className="space-y-4 max-w-lg">
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div>
            <label htmlFor="role_name" className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Role Name *</label>
            <input
              id="role_name"
              name="role_name"
              type="text"
              value={details.name}
              onChange={e => setDetails(d => ({ ...d, name: e.target.value }))}
              className="input-field w-full"
              placeholder="e.g. Advisor, Admin, Read Only"
            />
          </div>
          <div>
            <label htmlFor="role_description" className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Description</label>
            <textarea
              id="role_description"
              name="role_description"
              value={details.description}
              onChange={e => setDetails(d => ({ ...d, description: e.target.value }))}
              className="input-field w-full h-24 resize-none"
              placeholder="Describe this role..."
            />
          </div>
          <div className="pt-2">
            <Button variant="primary" onClick={handleSaveDetails} disabled={saving}>
              {saving ? 'Saving...' : savedRoleId ? 'Update & Next' : 'Save & Continue'}
            </Button>
          </div>
        </div>
      )}

      {/* Tab: Assigned Users */}
      {activeTab === 'users' && (
        <div className="space-y-2">
          {notSavedYet ? (
            <p className="text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Save role details first.</p>
          ) : advisorsLoading ? (
            <p className="text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Loading advisors...</p>
          ) : advisors.length === 0 ? (
            <p className="text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>No advisors found.</p>
          ) : (
            <>
              <p className="text-xs mb-4" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Toggle to assign or remove advisors from this role.</p>
              <div className="divide-y divide-gray-100">
                {advisors.map(advisor => {
                  const assigned = assignedAdvisors.has(advisor.id)
                  return (
                    <div key={advisor.id} className="flex items-center justify-between py-3">
                      <div>
                        <p className="text-sm font-medium" style={{ color: isDarkMode ? '#cbd5e1' : '#333333' }}>{advisor.full_name}</p>
                        <p className="text-xs" style={{ color: isDarkMode ? '#94a3b8' : '#999999' }}>{advisor.email}</p>
                      </div>
                      <Toggle checked={assigned} onChange={() => handleAdvisorToggle(advisor.id, assigned)} />
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Tab: Permissions (Hierarchical) */}
      {activeTab === 'permissions' && (
        <div className="space-y-4 overflow-y-auto" style={{ maxHeight: '60vh' }}>
          {notSavedYet ? (
            <p className="text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Save role details first.</p>
          ) : permsLoading ? (
            <p className="text-gray-400 text-sm">Loading permissions...</p>
          ) : (
            <>
              {/* Action Bar */}
              <div className="flex gap-2 flex-wrap items-center pb-3 border-b border-gray-200">
                <button
                  onClick={expandAll}
                  className="px-3 py-1.5 text-sm font-medium text-sfp-orange hover:bg-orange-50 rounded border border-orange-200"
                >
                  ▼ Expand All
                </button>
                <button
                  onClick={collapseAll}
                  className="px-3 py-1.5 text-sm font-medium text-sfp-orange hover:bg-orange-50 rounded border border-orange-200"
                >
                  ▲ Collapse All
                </button>

                {/* Legend */}
                <div className="flex gap-2 items-center ml-auto">
                  <div className="inline-flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-xs text-gray-600">All</span>
                  </div>
                  <div className="inline-flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <span className="text-xs text-gray-600">Some</span>
                  </div>
                  <div className="inline-flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-gray-400" />
                    <span className="text-xs text-gray-600">None</span>
                  </div>
                </div>
              </div>

              {/* Search */}
              <div className="relative">
                <input
                  id="permission_search"
                  name="permission_search"
                  type="text"
                  placeholder="Search permissions..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="input-field w-full pl-10"
                />
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">🔍</span>
              </div>

              {/* Modules and Sections */}
              <div className="space-y-1">
                {filteredModules.map(module => {
                  const moduleExpanded = expandedModules.has(module.id)
                  const modulePerms = getModulePermissionKeys(module.id)
                  const moduleStatus = getStatus(modulePerms, perms)

                  return (
                    <div key={module.id}>
                      {/* Module Header */}
                      <div className="flex items-center gap-2 p-3 rounded border transition" style={{ backgroundColor: isDarkMode ? '#334155' : '#f9fafb', borderColor: isDarkMode ? '#475569' : '#e5e7eb' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDarkMode ? '#475569' : '#f3f4f6'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isDarkMode ? '#334155' : '#f9fafb'}>
                        <button
                          onClick={() => toggleModule(module.id)}
                          className="flex-shrink-0 hover:text-sfp-orange"
                          style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}
                        >
                          {moduleExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                        <span className="flex-1 font-semibold text-sm" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>{module.name}</span>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${
                            moduleStatus === 'all' ? 'bg-green-500' :
                            moduleStatus === 'some' ? 'bg-yellow-500' :
                            'bg-gray-400'
                          }`} />
                          <label className="text-xs mr-1" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Allow</label>
                          <Toggle
                            checked={moduleStatus === 'all'}
                            onChange={(val) => handleModuleToggle(module.id, val)}
                          />
                        </div>
                      </div>

                      {/* Sections */}
                      {moduleExpanded && (
                        <div className="ml-4 mt-2 space-y-1 pl-2" style={{ borderLeft: `1px solid ${isDarkMode ? '#475569' : '#e5e7eb'}` }}>
                          {module.sections.filter(section =>
                            !search.trim() || section.name.toLowerCase().includes(search.toLowerCase()) ||
                            section.permissions.some(p => p.label.toLowerCase().includes(search.toLowerCase()))
                          ).map(section => {
                            const sectionExpanded = expandedSections.has(section.id)
                            const sectionPerms = getSectionPermissionKeys(section.id)
                            const sectionStatus = getStatus(sectionPerms, perms)

                            return (
                              <div key={section.id}>
                                {/* Section Header */}
                                <div className="flex items-center gap-2 p-2 rounded transition" style={{ color: isDarkMode ? '#cbd5e1' : '#333333' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDarkMode ? '#334155' : '#f3f4f6'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                  <button
                                    onClick={() => toggleSection(section.id)}
                                    className="flex-shrink-0 hover:text-sfp-orange"
                                    style={{ color: isDarkMode ? '#94a3b8' : '#999999' }}
                                  >
                                    {sectionExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  </button>
                                  <span className="flex-1 text-sm" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>{section.name}</span>
                                  <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${
                                      sectionStatus === 'all' ? 'bg-green-500' :
                                      sectionStatus === 'some' ? 'bg-yellow-500' :
                                      'bg-gray-400'
                                    }`} />
                                    <label className="text-xs mr-1" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Allow</label>
                                    <Toggle
                                      checked={sectionStatus === 'all'}
                                      onChange={(val) => handleSectionToggle(section.id, val)}
                                    />
                                  </div>
                                </div>

                                {/* Permissions */}
                                {sectionExpanded && (
                                  <div className="ml-6 mt-1 space-y-1 pl-2" style={{ borderLeft: `1px solid ${isDarkMode ? '#334155' : '#f0f0f0'}` }}>
                                    {section.permissions.filter(perm =>
                                      !search.trim() || perm.label.toLowerCase().includes(search.toLowerCase())
                                    ).map(perm => (
                                      <div key={perm.key} className="flex items-center justify-between py-1.5 px-2 rounded transition" style={{ color: isDarkMode ? '#cbd5e1' : '#333333' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDarkMode ? '#334155' : '#f3f4f6'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                        <label htmlFor={`perm-${perm.key}`} className="text-sm cursor-pointer" style={{ color: isDarkMode ? '#e2e8f0' : '#1f2937' }}>{perm.label}</label>
                                        <input
                                          id={`perm-${perm.key}`}
                                          name={`perm-${perm.key}`}
                                          type="checkbox"
                                          checked={!!perms[perm.key]}
                                          onChange={(e) => handlePermissionToggle(perm.key, e.target.checked)}
                                          className="rounded cursor-pointer w-4 h-4"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Save Button */}
              <div className="pt-4 flex items-center gap-3 border-t border-gray-200 mt-4">
                <Button variant="primary" onClick={handleSavePermissions} disabled={savingPerms}>
                  {savingPerms ? 'Saving...' : 'Save Permissions'}
                </Button>
                {permsSaved && !savingPerms && (
                  <span className="text-green-600 text-sm font-medium">✓ Saved</span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  )
}
